import type { BaseErrorInterface, BaseViewModelInterface, CollectionViewModelInterface, ExternalViewModelInterface, InternalViewModelInterface, ItemViewModelInterface, ModelInterface, ModifiedSubscriberInterface, PropertyMetaDataInterface, PropertyViewModelInterface, UICommandInterface, ViewModelEventDispatcherInterface, ViewModelInterface } from '@nts/std/interfaces';
import { BigNumber, ClassInformationType, DateTimeOffset, SourceMessage } from '@nts/std/types';
import { ClassInformationUtility, LogService } from '@nts/std/utility';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RequiredValidator } from '../domain-models/decorators/validations/required-validation';
import { DomainModelMetaData, MetaDataUtils } from '../meta-data';
import { AccessMode } from '../meta-data/access-mode.enum';
import { MessageResourceManager } from '../resources/message-resource-manager';
import { ValidationErrorCodes } from '../resources/validation-error-codes';
import { BasePropertyViewModel } from './base-property-view-model';
import { MessageContainer } from './message-container';
import { PropertyViewModelInitializationInfo } from './property-view-model-initialization-info';
import { PropertyViewModelPropertyChangedEventArgs } from './property-view-property-changed-event-args';
import { BaseError } from '@nts/std/messages';

// TODO Pensare nuova implementazione dei propertyViewModel
export abstract class PropertyViewModel<T> extends BasePropertyViewModel implements PropertyViewModelInterface<T> {

  abstract setDefaultValueFromLayoutMetaData(value: string | number | BigNumber | boolean | Date | DateTimeOffset): void;

  override isEnabled$ = new BehaviorSubject<boolean>(true);

  classType = ClassInformationType.PropertyViewModel;

  // ATTENZIONE: non abilitare, è necessario solo in casistiche estreme
  instantModelChange = false;

  // Utilizzata nelle external list pvm per aver la possibilità di evitare di notificare i cambiamenti
  canNotifyModified = true;

  customGetterValueChanged = new Subject<T>();
  customCommandList = new Map<string, UICommandInterface>();
  customCommandsName = [];
  decodePendingUpdated: Subject<boolean>;
  propertyMetaData: PropertyMetaDataInterface;
  domainModelMetadata: DomainModelMetaData;
  isInternalKey: boolean;
  isExtension = new BehaviorSubject<boolean>(false);
  componentIstance$: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  /**
   * utilizzata nei template dei componenti per evitare la detect changes
   */
  formattedValue$ = new BehaviorSubject<string>('');
  value$: BehaviorSubject<T> = new BehaviorSubject<T>(null);

  securityAccess: AccessMode | null = null;
  //darebbe da creare un external property view model con tutte le property dedicate all'external
  setIdentityOnSet = true;

  readonly useMessageResourceKey: boolean = false;

  additionalCustomValidation?: () => BaseError[];

  customSetter: (x: T) => Promise<void>;
  customGetter: () => T;

  protected internalDefaultColor: string = null;
  protected internalActiveColor: string = null;
  protected internalHoverColor: string = null;
  protected internalFaultBackValueFunc: null | (() => T);
  protected internalFaultBackValue: T;
  protected abstract getFormattedValue(value: T): string;
  protected _layoutDefaultValue: T = undefined;
  protected eventDispatcher: ViewModelEventDispatcherInterface;
  protected _isCustom = false;
  protected model: ModelInterface;

  private _customFormattedValueFunc: ((updatedValue: T) => string) | null = null;
  private internalFormattedValue: string;
  private internalIsNullable: boolean = false;
  private _modifiedSubscriber: ModifiedSubscriberInterface;
  private _decodePending = false;
  private _propertyPath: string;
  private _valueIsNullable: boolean;
  private _customGetterValue = null;
  private unsubscribeOldAdditionalObserver = new Subject<void>();

  #parent: ViewModelInterface;

  get defaultColor() {
    return this.internalDefaultColor;
  }
  set defaultColor(value: string) {
    if (this.internalDefaultColor !== value) {
      this.internalDefaultColor = value;
      this.onPropertyChanged('defaultColor');
    }
  }

  get activeColor() {
    return this.internalActiveColor;
  }
  set activeColor(value: string) {
    if (this.internalActiveColor !== value) {
      this.internalActiveColor = value;
      this.onPropertyChanged('activeColor');
    }
  }

  get hoverColor() {
    return this.internalHoverColor;
  }
  set hoverColor(value: string) {
    if (this.internalHoverColor !== value) {
      this.internalHoverColor = value;
      this.onPropertyChanged('hoverColor');
    }
  }

  override get isRequired() {
    return this.internalIsRequired;
  }
  override set isRequired(value: boolean) {
    if (this.internalIsRequired !== value) {
      this.internalIsRequired = value;
      this.validate();
      this.onPropertyChanged('isRequired');
      this.isRequired$.next(value);
    }
  }

  override get isEnabled(): boolean {
    // TODO
    // if (this.isInternalKey) {
    //   // (this.parent.parent as EntityViewModelInterface).getProperty => this.parent.parent is EntityViewModelInterface
    //   if (this.behavior.isExternal && this.parent.parent && (this.parent.parent as EntityViewModelInterface).getProperty) {
    //     return this._isEnabled && (<EntityViewModelInterface>this.parent.parent).currentState === ViewModelStates.New;
    //   } else if (this.behavior.wrappedEntity != null) {
    //     return this._isEnabled && this.behavior.wrappedEntity.currentState === State.New;
    //   }
    //   return this._isEnabled;
    // } else {
    return this._isEnabled;
  }
  override set isEnabled(value: boolean) {
    if (this._isEnabled !== value) {
      this._isEnabled = value;
      this.onPropertyChanged('isEnabled');
      this.isEnabled$.next(value);
    }
  }

  override get propertyPath(): string {
    return this._propertyPath;
  }

  get isCustom() {
    return this._isCustom;
  }

  get decodePending(): boolean {
    return this._decodePending;
  }
  set decodePending(value: boolean) {
    this._decodePending = value;
    this.decodePendingUpdated.next(value);
  }

  get value(): T {
    // Casistica in cui abbiamo il decoratore custom property view model impostato
    if (this._isCustom && this.customGetter == null) {
      throw new Error('You must implement value getter');
    }
    // Casistica in cui viene ridefinito il custom getter
    // Di default viene definito in tutti gli external view model
    const value = this.getValue();
    if (this.value$ && this.value$.value !== value) {
      this.value$.next(value);
    }
    return value;
  }

  set value(updatedValue: T) {
    if (this._isCustom) {
      const fullPathName = [this.propertyPath, this.propertyName].filter(x => x?.length > 0).join('.');

      LogService.warn(`Nel customPropertyViewModel '${fullPathName}' devi utilizzare setValueAsync al posto di .value = xxx, altrimenti avrai effetti indesiderati`);

      // Per supportare il codice legacy, continuiamo a utilizzare erroneamente il metodo senza await
      // In futuro dovrebbe essere rimosso.
      void this.setValueAsync(updatedValue);
    } else {
      this.setValueSync(updatedValue);
    }
  }

  setFaultBackValueFunc(func: () => T): void {
    this.internalFaultBackValueFunc = func;
  }

  get faultBackValueFunc(): null | (() => T) {
    return this.internalFaultBackValueFunc;
  }

  get faultBackValue() {
    if (this.faultBackValueFunc) {
      return this.faultBackValueFunc();
    }
    return this.internalFaultBackValue;
  }

  setFaultBackValue(value: T) {
    this.internalFaultBackValue = value;
  }

  get layoutDefaultValue(): T {
    return this._layoutDefaultValue;
  }

  get formattedValue(): string {
    return this.internalFormattedValue;
  }

  updateFormattedValue() {
    this.internalFormattedValue = this.getFormattedValue(this.value);
    if (this.formattedValue$.value !== this.formattedValue) {
      this.formattedValue$.next(this.formattedValue);
    }
  }

  get isNullable(): boolean {
    return this.internalIsNullable;
  }
  set isNullable(value: boolean) {
    if (this.internalIsNullable !== value) {
      this.internalIsNullable = value;
      this.onPropertyChanged('isNullable');
    }
  }

  constructor(initInfo: PropertyViewModelInitializationInfo, valueIsNullable: boolean) {
    super(initInfo.propertyName);

    this.useMessageResourceKey = initInfo.useMessageResourceKey ?? false;

    this.customGetter = initInfo.customGetter;
    this.customSetter = initInfo.customSetter;

    if (initInfo?.propertyMetaData?.descriptions) {
      this.metadataDescription = this.useMessageResourceKey ?
        MessageResourceManager.Current.getMessage(initInfo.propertyMetaData.descriptions.descriptionKey) :
        initInfo.propertyMetaData.descriptions.description;
    }

    if (initInfo?.propertyMetaData?.descriptions) {
      this.metadataShortDescription = this.useMessageResourceKey ?
        MessageResourceManager.Current.getMessage(initInfo.propertyMetaData.descriptions.displayNameKey) :
        initInfo.propertyMetaData.descriptions.displayName;
    }

    this.domainModelMetadata = initInfo.domainModelMetadata;
    this.propertyMetaData = initInfo.propertyMetaData;
    this._valueIsNullable = valueIsNullable;
    this._modifiedSubscriber = initInfo.modifiedSubscriber;
    this.eventDispatcher = initInfo.eventDispatcher;
    this.model = initInfo.model;
    if (this.model != null) {
      this.model.propertyChanged.pipe(takeUntil(this.destroySubscribers$), takeUntil(this.modelChanged)).subscribe((propertyName) => {
        this.modelPropertyChanged(propertyName);
      });
    }
    this.decodePendingUpdated = new Subject<boolean>();
    this._isCustom = initInfo.isCustom;
    this.internalIsRequired = initInfo.propertyMetaData?.isRequired || false;
    this.isInternalKey = this.checkIsInternalKey(initInfo);
    this.isExtension.next(initInfo?.propertyMetaData?.isExtension || false);
    this.parent = initInfo.parent;
    this._propertyPath = initInfo.path;

    if (
      initInfo.isCustom
      && initInfo.parent != null
      && initInfo.parent.propertyViewModels != null
    ) {
      initInfo.parent.propertyViewModels.set(initInfo.propertyName, this);
    }
  }

  override onDestroy() {
    super.onDestroy();

    // this.classType = null;
    // this.instantModelChange = null;

    // this.canNotifyModified = null;

    this.customGetterValueChanged?.complete();
    this.customGetterValueChanged = null;

    this.customCommandList?.clear();
    // this.customCommandList = null;

    // this.customCommandsName = null;

    this.decodePendingUpdated?.complete();
    // this.decodePendingUpdated = null;

    // this.propertyMetaData = null;
    // this.domainModelMetadata = null;

    this.isInternalKey = null;
    this.isExtension?.complete();
    // this.isExtension = null;

    this.componentIstance$?.complete();
    // this.componentIstance$ = null;

    this.formattedValue$?.complete();
    // this.formattedValue$ = null;

    this.value$?.complete();
    // this.value$ = null;

    // this.securityAccess = null;
    // this.setIdentityOnSet = null;

    this.additionalCustomValidation = null;
    this.customSetter = null;
    this.customGetter = null;

    // this.internalDefaultColor = null;
    // this.internalActiveColor = null;
    // this.internalHoverColor = null;
    this.internalFaultBackValueFunc = null;
    // this.internalFaultBackValue = null;
    // this._layoutDefaultValue = null;

    // this.eventDispatcher = null;
    // this._isCustom = null;
    // this.model = null;

    this._customFormattedValueFunc = null;
    // this.internalFormattedValue = null;
    // this.internalIsNullable = null;
    // this._modifiedSubscriber = null;

    // this._decodePending = null;
    // this._propertyPath = null;
    // this._valueIsNullable = null;
    // this._customGetterValue = null;
    this.unsubscribeOldAdditionalObserver?.complete();
    // this.unsubscribeOldAdditionalObserver = null;

    this.#parent = null;
  }

  override get parent() {
    return this.#parent;
  }

  override set parent(value: ViewModelInterface) {
    this.#parent = value;
  }

  get propertyPathWithIndex(): string {
    return this.recursiveFindPropertyPathWithIndex(this.parent).join('.');
  }

  /**
   * Se si vuole fare in modo che la pvm triggeri il property changed quando accadono dei cambiamanti esterni alla property
   * Ad esempio se si utilizza un custom getter che punta ad altre property ed è necessario triggerare il ppoperty change quando scattano i property changed delle altre property
   *
   * @param additionalObserver
   */
  setAdditionalObserverForValuePropertyChange(additionalObserver: Observable<any>) {
    this.unsubscribeOldAdditionalObserver.next();
    additionalObserver.pipe(
      takeUntil(this.destroySubscribers$),
      takeUntil(this.unsubscribeOldAdditionalObserver)
    ).subscribe(() => {
      this.onPropertyChanged(this.bindedValuePropertyName);
    });
  }

  private recursiveFindPropertyPathWithIndex(viewModel: BaseViewModelInterface, path = []): string[] {
    if (ClassInformationUtility.isRootViewModel(viewModel)) {
      return path;
    }
    if (ClassInformationUtility.isPropertyViewModel(viewModel)) {
      path.unshift((viewModel as PropertyViewModelInterface).propertyName);
    }
    if (ClassInformationUtility.isItemViewModel(viewModel)) {
      path.unshift((viewModel.parent as CollectionViewModelInterface<any>).indexOf(viewModel as ItemViewModelInterface));
    } else if (ClassInformationUtility.isCollectionViewModel(viewModel)) {
      path.unshift(MetaDataUtils.toCamelCase((viewModel as CollectionViewModelInterface<any>).collectionMetaData.principalPropertyName));
    } else if (ClassInformationUtility.isInternalViewModel(viewModel)) {
      path.unshift(MetaDataUtils.toCamelCase((viewModel as InternalViewModelInterface).relationMetaData.principalPropertyName));
    } else if (ClassInformationUtility.isExternalViewModel(viewModel)) {
      path.unshift(MetaDataUtils.toCamelCase((viewModel as ExternalViewModelInterface).externalMetaData.principalPropertyName));
    } else if (ClassInformationUtility.isMasterViewModel(viewModel)) {
      return path;
    }

    return this.recursiveFindPropertyPathWithIndex(viewModel.parent, path);
  }

  override clearErrors() {
    if (this.hasErrors) {
      this.removeAllMessages();
      this.propertyViewModelChanged.emit();
    }
  }

  override onPropertyChanged(propertyName: string = null) {
    const args = new PropertyViewModelPropertyChangedEventArgs();
    args.propertyName = propertyName;
    if (propertyName === this.bindedValuePropertyName) {

      if (
        this._modifiedSubscriber != null && this.canNotifyModified &&

        // Se sono in un modello mockato non devo notificare il modifiedSubscriber
        (this?.model?.isMock == null || this?.model?.isMock == false)
      ) {
        this._modifiedSubscriber.notifyModified();
      }
      args.value = this.value;
      this.value$.next(this.value);
    }
    this.updateFormattedValue();
    this.propertyChanged.emit(args);
  }

  onModelChanged() {
    this.modelChanged.emit();
  }

  validateCustomPropertyViewModel(baseErrors: Array<BaseErrorInterface>): boolean {
    return true;
  }

  applyRequiredLogic(baseErrors: BaseErrorInterface[]) {
    // rimuovo gli errori del required aggiunti
    baseErrors = baseErrors.filter((e) => e.code !== ValidationErrorCodes.IsRequired && e.code !== ValidationErrorCodes.IsDefined);

    if (this.isRequired == true) {
      const validator = new RequiredValidator(this.metadataShortDescription);
      if (!validator.validate(this.value)) {
        const e = new BaseError();
        e.description = validator.errorMessage;
        e.code = validator.messageCode;
        e.propertyName = this.propertyName;
        e.objectName = this.model?.modelTypeName;
        baseErrors.push(e);
        return baseErrors;
      }
    }
    return baseErrors;
  }

  getModelErrors(propertyName: string) {
    let baseErrors: BaseErrorInterface[] = [];
    this.model.validateProperty(propertyName, baseErrors);

    baseErrors = this.applyRequiredLogic(baseErrors);

    if (this.additionalCustomValidation) {
      const additionalCustomValidationErrors = this.additionalCustomValidation();
      if (additionalCustomValidationErrors?.length > 0) {
        return [...baseErrors, ...additionalCustomValidationErrors];
      }
    }

    return baseErrors;
  }

  override validate() {
    if (this.skipValidation || this.model?.isMock === true) {
      return;
    }

    const value = this.value;
    const validationMessages = this.messageContainerCollection;
    let previousError = false;
    const itemsToRemove = validationMessages.filter(x => x.sourceMessage === SourceMessage.Validation || x.sourceMessage === SourceMessage.Api);

    itemsToRemove.forEach((itemToRemove) => {
      validationMessages.splice(validationMessages.indexOf(itemToRemove), 1);
      previousError = true;
    });

    if (this.model != null) {
      // valido il campo
      const baseErrors = this.getModelErrors(this.propertyName);

      if (baseErrors.length > 0) {

        baseErrors.forEach(e => {
          this.addErrorMessageIfNotExist(e, SourceMessage.Validation, false);
        });

        if (this['eventDispatcher']) {
          const ed = (this as any).eventDispatcher as ViewModelEventDispatcherInterface;
          ed.onAddMessageInViewModel.next({ viewModel: this, messages: baseErrors });
        }
        // this.raiseErrorsChanged(this.propertyViewModel.bindedValuePropertyName);

      } else if (previousError) { // && !validationErrors.ContainsKey(this.PropertyViewModel.BindedValuePropertyName)

        if (this['eventDispatcher']) {
          const ed = (this as any).eventDispatcher as ViewModelEventDispatcherInterface;
          ed.onClearMessagesInViewModel.next(this);
        }

        // this.raiseErrorsChanged(this.propertyViewModel.bindedValuePropertyName);
      }
      this.updateCurrentErrors();
      this.onErrorStatusChanged.next();
    } else {
      let baseErrors = this.applyRequiredLogic([]);

      if (this.isCustom) {
        this.validateCustomPropertyViewModel(baseErrors);

        if (this.additionalCustomValidation) {
          const additionalCustomValidationErrors = this.additionalCustomValidation();
          if (additionalCustomValidationErrors?.length > 0) {
            baseErrors = [...baseErrors, ...additionalCustomValidationErrors];
          }
        }

        if (baseErrors.length > 0) {

          baseErrors.forEach(e => {
            this.addErrorMessageIfNotExist(e, SourceMessage.Validation, false);
          });

          if (this['eventDispatcher']) {
            const ed = (this as any).eventDispatcher as ViewModelEventDispatcherInterface;
            ed.onAddMessageInViewModel.next({ viewModel: this, messages: baseErrors });
          }
          // this.raiseErrorsChanged(this.propertyViewModel.bindedValuePropertyName);

        }
      } else if (previousError) { // && !validationErrors.ContainsKey(this.PropertyViewModel.BindedValuePropertyName)

        if (this['eventDispatcher']) {
          const ed = (this as any).eventDispatcher as ViewModelEventDispatcherInterface;
          ed.onClearMessagesInViewModel.next(this);
        }

        // this.raiseErrorsChanged(this.propertyViewModel.bindedValuePropertyName);
      }
      this.updateCurrentErrors();
      this.onErrorStatusChanged.next();
    }
  }

  modelPropertyChanged(propertyName: string) {
    if (propertyName === this.propertyName) {
      this.validate();
      this.onPropertyChanged(this.bindedValuePropertyName);
    }
  }

  setCodeOnExternal(isCode: boolean) {
    // TODO Tommy
    // (<ExternalPropertyViewModelBehavior<T>>this.behavior).isCodeProperty = isCode;
  }

  setModel(model: ModelInterface) {
    this.model = model;
    this.onModelChanged();
    if (this.model != null) {
      this.model.propertyChanged.pipe(takeUntil(this.destroySubscribers$), takeUntil(this.modelChanged)).subscribe((propertyName) => {
        this.modelPropertyChanged(propertyName);
      });
    }
    this.onPropertyChanged(this.bindedValuePropertyName);
  }

  getModel() {
    return this.model;
  }

  async resetValue(useDefaultValue: boolean): Promise<void> {
    if (useDefaultValue) {
      if (typeof this.value === 'string') {
        await this.setValueAsync('' as any);
      } else if (typeof this.value === 'number') {
        await this.setValueAsync(0 as any);
      } else if (typeof this.value === 'boolean') {
        await this.setValueAsync(false as any);
      } else {
        await this.setValueAsync(undefined);
        LogService.warn('Default Value Not Implemented for property: ' + this.propertyName);
      }
    } else {
      await this.setValueAsync(this.faultBackValue);
    }
  }

  // TODO Tommy
  checkIsInternalKey(initInfo: PropertyViewModelInitializationInfo): boolean {
    return false;
    // let res = false;
    // if (initInfo.entityMetadata != null && initInfo.entityMetadata.identityProperties.length) {

    //   TODO: ATTENZIONE toPascalCase non è simmetrico a CamelCase
    //   res = initInfo.entityMetadata.identityProperties.indexOf(MetaDataUtils.toPascalCase(initInfo.propertyName)) !== -1;
    //   if (!res) {
    //     // proprietà di navigazione che usa una parte della identity
    //     TODO: ATTENZIONE toPascalCase non è simmetrico a CamelCase
    //     const ext = initInfo.entityMetadata.externalRelations.find(x => x.propertyName === MetaDataUtils.toPascalCase(initInfo.propertyName));
    //     if (ext != null) {
    //       res = (initInfo.entityMetadata.identityProperties.find(x => ext.principalAssociationProerties.indexOf(x) !== -1) != null);
    //     }
    //   }
    // }
    // return res;
  }

  syncModel(value: T) {
    if (this.model != null) {
      if (this.isExtension.value) {
        this.model.setDynamicPropertyValue(this.propertyName, value);
      } else {
        this.model.setPropertyValue(this.propertyName, value);
      }

    }
    //  else if (this.faultBackValue !== value) {
    //     this.faultBackValue = value;
    // }
  }

  private setValueSync(updatedValue: T) {
    if (!this.isNullable) {
      if (updatedValue == null) {
        updatedValue = this.faultBackValue;
      }
    }

    // Check undefined and null
    if (this.value != updatedValue) {
      // quano faccio il sync model scatta onPropertyChanged partendo dal modello
      this.syncModel(updatedValue);
      this.validate();

      this.formattedValue$.next(this.formattedValue);

      // Aggiorno il valore per gli ascoltatori
      this.value$.next(this.value);
    }
  }

  async setValueAsync(updatedValue: T) {
    if (this._isCustom && this.customSetter == null) {
      throw new Error('You must implement value setter');
    }

    if (!this.isNullable) {
      if (updatedValue == null) {
        updatedValue = this.faultBackValue;
      }
    }

    // Check undefined and null
    if (this.value != updatedValue) {
      if (this._isCustom) {
        await this.customSetter(updatedValue);

        // Se il custom setter ha impostato updatedValue in this.value faccio scattare la property changed e la validate altrimenti non faccio niente
        if (this.value == updatedValue) {
          this.validate();
          this.onPropertyChanged(this.bindedValuePropertyName);
        } else {
          this.onPropertyChanged(this.bindedValuePropertyName);
        }

      } else {
        // quano faccio il sync model scatta onPropertyChanged partendo dal modello
        this.syncModel(updatedValue);
        this.validate();
      }

      // TODO verificare se si può rimuovere
      this.formattedValue$.next(this.formattedValue);

      // Aggiorno il valore per gli ascoltatori
      this.value$.next(this.value);
    }
  }

  // Da usare solo nei custom getter
  internalGetValue(): T {
    if (this.isExtension?.value) {
      if (this.model == null) {
        return this.faultBackValue;
      }
      return this.model.getDynamicPropertyValue<T>(this.propertyName) ?? this.faultBackValue;
    } else {
      return this.model != null ?
        this.model.getPropertyValue<T>(this.propertyName) :
        this.faultBackValue;
    }
  }

  getValue(): T {
    if (this.customGetter != null) {
      let value = this.customGetter();

      // Se la property NON e' nullable e il valore e' nullo, ignoriamo le logiche del customGetter
      // e associamo il valore del faultBack
      if (this._valueIsNullable == false && value == null) {
        value = this.faultBackValue;
      }

      if (this.compareValues(this._customGetterValue, value, '!=')) {
        this._customGetterValue = value;
        setTimeout(
          () => {
            if (this.customGetterValueChanged != null) {
              this.customGetterValueChanged.next(this._customGetterValue);
            }
          }
        );
      }

      // this.onPropertyChanged(this.bindedValuePropertyName);
      return this._customGetterValue;
    } else {
      return this.internalGetValue();
    }
    // return this.customGetter != null ? this.customGetter() : this.internalGetValue();
  }

  compareValues(v1: T, v2: T, operator: string = '=='): boolean {
    switch (operator) {
      case '>':
        return v1 > v2;
      case '>=':
        return v1 >= v2;
      case '<':
        return v1 < v2;
      case '<=':
        return v1 <= v2;
      case '==':
        return v1 === v2;
      case '!=':
        return v1 != v2;
      default:
        throw new Error(`not implemented sign ${operator} in compareValues`);
    }
  }

  setValue(value: any) {
    this.value = value as T;
  }

  async setCurrentValueWithDefaultValueFromLayoutMetaData(): Promise<void> {
    if (this.layoutDefaultValue !== undefined) {
      const oldValue = this.canNotifyModified;
      this.canNotifyModified = false;
      await this.setValueAsync(this.layoutDefaultValue);
      this.canNotifyModified = oldValue;
    }
  }

  setErrors(sourceMessage: SourceMessage, errorList: Array<string>) {
    if (errorList.length > 0) {
      this.removeMessagesFromSource(sourceMessage);
      const errors = errorList.map(item => {
        const error = new BaseError();
        error.description = item;
        error.propertyName = this.propertyName;
        this.messageContainerCollection.splice(
          0, 0, MessageContainer.fromBaseAndSourceMessage(error, sourceMessage, this.uniqueId));
        return error;
      });
      if (this['eventDispatcher']) {
        const ed = (this as any).eventDispatcher as ViewModelEventDispatcherInterface;
        ed.onAddMessageInViewModel.next({ viewModel: this, messages: errors });
      }
    } else if (this.messageContainerCollection.findIndex(x => x.sourceMessage === sourceMessage) > -1) {
      // rimuovi tutti gli elementi del tipo sourceMessage
      this.removeMessagesFromSource(sourceMessage);
      if (this['eventDispatcher']) {
        const ed = (this as any).eventDispatcher as ViewModelEventDispatcherInterface;
        ed.onRemovedMessageInViewModel.next(this);
      }
    }
    // this.propertyViewModelChanged.emit();
    this.updateCurrentErrors();
    this.onErrorStatusChanged.next();
  }

  get customFormattedValueFunc() {
    return this._customFormattedValueFunc;
  }
  set customFormattedValueFunc(val: ((updatedValue: T) => string) | null) {
    this._customFormattedValueFunc = val;
    this.updateFormattedValue();
  }
}
